import { useState, useEffect, useCallback, useRef } from 'react'
import { api } from '@/api/client'
import { getCableConsumer, waitForCableAuth } from '@/api/cable'
import { OfflineError } from '@/lib/offline-error'
import type { Subscription } from '@rails/actioncable'
import type { ReadinessResponse, SystemState } from '@/api/types'

interface LedgerState {
  readiness: ReadinessResponse | null
  loading: boolean
  error: string | null
  systemState: SystemState
  isReady: boolean
  refresh: () => Promise<void>
}

const FALLBACK_POLL_MS = 30_000
const TOKEN_KEY = 'ledger_core_token'

export function useLedgerState(): LedgerState {
  const [readiness, setReadiness] = useState<ReadinessResponse | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const subRef = useRef<Subscription | null>(null)
  const fallbackRef = useRef<ReturnType<typeof setInterval> | null>(null)
  const cancelledRef = useRef(false)

  const refresh = useCallback(async () => {
    const t0 = performance.now()
    try {
      const data = await api.getReadiness()
      api.recordPing(Math.round(performance.now() - t0))
      setReadiness(data)
      setError(null)
    } catch (e) {
      api.recordPingFailure()
      // If we have a stored token and the API is unreachable, assume
      // the system was ready before we went offline - don't block the
      // entire UI with an uninitialised/genesis gate.
      if (api.isAuthenticated() && (e instanceof OfflineError || OfflineError.isNetworkError(e))) {
        setReadiness({ state: 'ready', ready: true } as ReadinessResponse)
        setError('offline')
      } else {
        setError(e instanceof Error ? e.message : 'Failed to connect to Ledger. Core API')
      }
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    cancelledRef.current = false

    // Initial fetch via HTTP so we have data immediately
    refresh()

    const orgId = api.currentOrgId
    if (!orgId) return

    // Wait for ApiChannel to authenticate, then subscribe to LedgerChannel
    waitForCableAuth().then(() => {
      if (cancelledRef.current) return

      const consumer = getCableConsumer()
      subRef.current = consumer.subscriptions.create(
        { channel: 'LedgerChannel', organisation_id: orgId },
        {
          received(data: unknown) {
            const msg = data as { type?: string; data?: ReadinessResponse }
            if (msg?.type === 'readiness' && msg.data) {
              setReadiness(msg.data)
              setError(null)
            }
          },
          connected() {
            // Socket connected - send inline auth for this channel
            const token = localStorage.getItem(TOKEN_KEY)
            if (token && subRef.current) {
              subRef.current.perform('authenticate', { token })
            }
            // Clear fallback polling
            if (fallbackRef.current) {
              clearInterval(fallbackRef.current)
              fallbackRef.current = null
            }
          },
          disconnected() {
            if (!fallbackRef.current) {
              fallbackRef.current = setInterval(refresh, FALLBACK_POLL_MS)
            }
          },
          rejected() {
            if (!fallbackRef.current) {
              fallbackRef.current = setInterval(refresh, FALLBACK_POLL_MS)
            }
          },
        }
      )
    })

    return () => {
      cancelledRef.current = true
      subRef.current?.unsubscribe()
      if (fallbackRef.current) clearInterval(fallbackRef.current)
    }
  }, [refresh])

  return {
    readiness,
    loading,
    error,
    systemState: readiness?.state || 'uninitialised',
    isReady: readiness?.ready || false,
    refresh,
  }
}
